import { api, ApiError } from '../../../core/network/api';
import { playSample } from '../menu-sfx';
import { renderAsync } from '../async-view';
import { refreshPlayerProgress } from '../player-progress-store';
import { isProfileNameRequired, promptProfileName } from '../profile-name-prompt';
import { badge, button, card, confirmAction, icon, openModal, sectionHeading, tabStrip, type IconName } from '../ui';
import { revealOnMount, rewardBurst } from '../fx';
import { skinStillPath, stillImg } from './renders';
import { createTitlesSection } from './shop-titles';
import { createSellSection } from './shop-sell';
import { createWeaponSkinsSection } from './shop-weapon-skins';
import { createCharmsSection } from './shop-charms';
import { createSpraysSection } from './shop-sprays';
import { createEmotesSection } from './shop-emotes';
import { createPlayerCardsSection } from './shop-player-cards';
import {
  MARKET_GRID_CLASS,
  OPERATOR_GRID_CLASS,
  buyButton,
  marketTile,
  ownedStamp,
  priceChip,
  rarityRibbon,
  rarityWash,
  type MarketCategoryId,
} from './marketplace-tile';
import type { MarketSection } from './marketplace-section';
import {
  fetchTopupPackages,
  fetchTopupSessionStatus,
  type TopupCustomTerms,
  type TopupPackage,
  type TopupPackagesView,
} from '../topup-api';
import { getPaymentMethods, createDeposit } from '../../../core/payments/payments-api';
import { collectBrowserInfo } from '../../../core/payments/methods';
import { mountPaymentMethodPicker, type PaymentMethodPickerHandle } from '../../../core/payments/widgets/payment-method-picker';
import { mountCurrencySelector, type CurrencySelectorHandle } from '../../../core/payments/widgets/currency-selector';
import {
  readReturnedOrderId, clearPendingOrder, PENDING_ORDER_KEY, pollDepositStatus,
} from '../../../core/payments/deposit-flow';
import type { PaymentMethodsResponse } from '../../../core/payments/types';
import { RANGE_CLASS_GEMS, syncRangeFill, thumbCenterPx } from '../range';
import type { RouteMount } from './types';
import { formatNumber, t } from '../../i18n';

/**
 * The Shop route — the storefront view of the whole cosmetics catalogue, rebuilt as one
 * marketplace (2026-09-16, Arturs: "красивый UI маркетплейса").
 *
 * A category rail (`tabStrip`) sits across the top: All / Operators / Weapon Finishes / Charms /
 * Sprays / Emotes / Player Cards / Titles. "All" is a curated front page — the full Operators
 * grid (the shop's main stock, unchanged) plus a short preview row from every other category
 * that has anything to show; any other tab narrows to that one category's full grid. Six of the
 * seven categories (everything but Operators) share ONE tile shape and ONE grid class
 * (`marketplace-tile.ts`) — the visual proof that this is one store, not several lists stapled
 * together. Operators keeps its own full-bleed banner treatment: it is the shop's main stock,
 * already had the reference's best-looking card, and the brief is explicit that it must keep
 * working exactly as it does.
 *
 * Operators reads `GET /cosmetics` / `POST /cosmetics/purchase` — the same API the armoury
 * reads, split by intent rather than by data (see the note that used to live here: the armoury
 * is "your kit" with a 3D preview, the shop is "what can I spend on", price first, no Three.js
 * chunk). The other six categories each own their own fetch (`marketplace-section.ts`): a dead
 * category hides its own tab rather than blanking the store, and a busy purchase in one category
 * never locks another.
 */
interface ShopEntry {
  id: number;
  name: string;
  description: string;
  /** CSS colour, formatted server-side. */
  accent: string;
  unlock:
    | { kind: 'default' }
    | { kind: 'level'; level: number; gemCost: number }
    // `points`, not `rankPoints` — the server's own field name
    // (`backend/src/game/cosmetics/cosmetics.types.ts`).
    | { kind: 'rank'; points: number; gemCost: number }
    | { kind: 'purchase'; gemCost: number };
  owned: boolean;
  equipped: boolean;
  affordable: boolean;
}

interface ShopCatalogue {
  progress: { level: number; rankPoints: number; softBalance: number; premiumBalance: number };
  skins: ShopEntry[];
}

/** Deals a grid's children in: staggered `kit-deal`. */
function deal(container: HTMLElement): void {
  [...container.children].forEach((child, index) => {
    if (!(child instanceof HTMLElement)) return;
    child.style.setProperty('--i', String(index));
    child.classList.add('kit-deal');
  });
}

/** A line of feedback cut into the window, not floating on it. `error` is a failed action; `dim`
 * is information. */
function notice(message: string, tone: 'error' | 'dim'): HTMLElement {
  const el = document.createElement('p');
  el.className = `kit-well px-3 py-2 text-[13px] ${tone === 'error' ? 'text-error' : 'text-text-dim'}`;
  el.textContent = message;
  return el;
}

/** The painted icon on each rail tab. A tab id with no entry gets no icon. */
const TAB_ART: Record<string, IconName> = {
  all: 'shop',
  operators: 'squad',
  gems: 'premium',
  weaponSkins: 'armoury',
  charms: 'star',
  sprays: 'tools',
  emotes: 'sound',
  playerCards: 'career',
  titles: 'trophy',
  sell: 'credits',
};

function priceLabel(entry: ShopEntry): string {
  // No unit suffix: the gem icon beside it is the unit now. Kept a function because the
  // shop's own tests read this label. Every non-default skin has a gem price (2026-09-09).
  return entry.unlock.kind === 'default' ? '—' : formatNumber(entry.unlock.gemCost);
}

/** Presentation tiering only — the catalogue has no rarity concept; this maps how hard a skin
 * is to get onto the rarity-gradient utilities so the grid reads "these differ in value" (§4).
 * Never gameplay. Kept identical to `armoury.ts`'s own mapping: the same skin appears in both
 * screens, and one that is teal here and violet there teaches that the colour means nothing. */
function rarityFor(entry: ShopEntry): 'common' | 'rare' | 'epic' | 'legendary' {
  switch (entry.unlock.kind) {
    case 'default':
      return 'common';
    case 'purchase':
      return entry.unlock.gemCost >= 2750 ? 'epic' : 'rare';
    case 'level':
      return 'epic';
    case 'rank':
      return 'legendary';
  }
}

/** The free route to a gated skin, for the lock plate: play. The gem price sits beside it. */
function requirementLabel(entry: ShopEntry): string {
  switch (entry.unlock.kind) {
    case 'level':
      return t('common.unlockLevel', { level: entry.unlock.level });
    case 'rank':
      return t('common.unlockPoints', { points: entry.unlock.points });
    default:
      return t('shop.starter');
  }
}

/**
 * An operator plate: the character render LARGE in a well lit by its rarity, the name in game
 * lettering under it, then one footer — a gem price chip and a gold BUY. Art first, words second.
 */
function shopTile(
  entry: ShopEntry,
  busySkinId: number | null,
  onBuy: (id: number) => void,
): HTMLElement {
  const rarity = rarityFor(entry);
  // Everything but the starter kit is for sale in Gems (2026-09-09); a level or rank skin
  // additionally shows its free route above the footer.
  const purchasable = entry.unlock.kind !== 'default';
  const gated = entry.unlock.kind === 'level' || entry.unlock.kind === 'rank';

  const tile = document.createElement('div');
  tile.dataset.shop = `tile-${entry.id}`;
  // `group`: the still's scale reacts to hovering the PLATE, not itself. Owned gets the teal rim.
  tile.className = [
    'group kit-plate kit-plate-hit [--kp-rim:14px] [--kp-pad:12px] w-full min-w-0 flex flex-col gap-2',
    entry.owned ? 'kit-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');
  tile.title = entry.description;

  const well = document.createElement('div');
  well.className = 'kit-well relative overflow-hidden aspect-[3/4] max-h-[340px] w-full';
  // The render fills the well, feet at the bottom edge. The stills are 384x512 with the figure
  // inset in its own frame, so the image is oversized and bottom-anchored (the well clips): at
  // `h-[88%]` the character came out a small figure with a third of the frame empty above it.
  // Absent still (a new catalogue entry before `tools/ui-stills/` re-ran): the accent becomes a
  // wash instead, so the plate never collapses.
  const art = stillImg({
    src: skinStillPath(entry.id),
    alt: entry.name,
    width: 384,
    height: 512,
    className:
      'absolute inset-x-0 bottom-0 mx-auto h-[112%] w-auto object-contain object-bottom origin-bottom ' +
      'transition-transform duration-200 ease-out motion-safe:group-hover:scale-[1.04] ' +
      '[filter:drop-shadow(0_10px_14px_rgba(0,0,0,0.65))]',
    fallback: () => {
      const wash = document.createElement('div');
      wash.className = 'absolute inset-0 opacity-30';
      wash.style.background = `radial-gradient(ellipse at 50% 70%, ${entry.accent}, transparent 70%)`;
      return wash;
    },
  });
  well.append(rarityWash(rarity), art, rarityRibbon(rarity));

  // Tagged so a successful purchase can find its own mark and pulse it, rather than the purchase
  // handler having to know this element's internal shape.
  if (entry.owned) {
    const stamp = ownedStamp(entry.equipped ? t('common.equipped') : t('common.owned'));
    stamp.dataset.shop = `badge-${entry.id}`;
    well.append(stamp);
  }
  if (entry.equipped) {
    const worn = badge(t('common.equipped'), 'success');
    worn.className += ' absolute bottom-1.5 left-1/2 -translate-x-1/2';
    well.append(worn);
  }
  tile.append(well);

  // ── The bottom stack: name, description, free route, footer — one flow, never absolute offsets.
  //
  // These used to be positioned independently off the card's bottom edge, which works for exactly
  // the content those numbers were measured against: a level-gated skin's price row wrapped,
  // grew upward into the description, and the text read through the word OR (2026-09-18). A
  // column that stacks cannot overlap itself.
  const stack = document.createElement('div');
  stack.dataset.shop = 'stack';
  stack.className = 'flex flex-col items-center gap-1.5 min-w-0 flex-1 text-center';

  const name = document.createElement('div');
  name.className = 'kit-label font-heading uppercase text-[17px] leading-none break-words max-w-full';
  name.textContent = entry.name;
  stack.append(name);

  // The description sits ON the plate: a tooltip alone is invisible to every touch device.
  const blurb = document.createElement('span');
  blurb.className = 'text-[11px] leading-tight text-text-dim line-clamp-2';
  blurb.textContent = entry.description;
  stack.append(blurb);

  if (gated && !entry.owned) {
    // The free route on its OWN line above the footer, not trailing the price, which is what
    // wrapped the row on a ~200px card.
    const free = document.createElement('span');
    free.className = 'text-[11px] text-text-dim truncate max-w-full';
    free.textContent = t('shop.orRequirement', { requirement: requirementLabel(entry) });
    stack.append(free);
  }

  const footer = document.createElement('div');
  footer.className = 'mt-auto flex flex-wrap items-center justify-center gap-2 min-w-0 pt-1';
  if (purchasable) {
    footer.append(priceChip('PREMIUM', priceLabel(entry)));
  } else if (!entry.owned) {
    // Not for sale at all: the requirement on a padlocked chip, and nothing to press.
    const lock = document.createElement('span');
    lock.className = 'kit-chip [--kh:30px] max-w-full';
    lock.append(icon('locked', { size: 22, className: '-ml-2 [filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' }));
    const need = document.createElement('span');
    need.className = 'kit-label font-heading text-[12px] truncate';
    need.textContent = requirementLabel(entry);
    lock.append(need);
    footer.append(lock);
  }

  if (entry.owned) {
    // Hidden on a phone, where two cards share a 375px row and the footer cannot hold a price
    // and a sentence: the tick in the well already says it.
    const hint = document.createElement('span');
    hint.className = 'hidden min-[520px]:inline text-[11px] text-text-dim shrink-0 whitespace-nowrap';
    hint.textContent = t('shop.equipInArmoury');
    footer.append(hint);
  } else if (purchasable) {
    // A busy purchase is legible in two ways: this button's own label swaps to "Buying…" and
    // disables, and every OTHER Buy button on the grid also disables (`blockedByOther`) so a
    // second, unrelated click does not appear to silently do nothing while the first is in flight.
    const isBusyHere = busySkinId === entry.id;
    const blockedByOther = busySkinId !== null && !isBusyHere;
    const opts = {
      disabled: !entry.affordable || isBusyHere || blockedByOther,
      className: 'px-5',
      onClick: () => onBuy(entry.id),
    };
    const buy = entry.affordable
      ? buyButton(isBusyHere ? t('common.buying') : t('common.buy'), opts)
      : button(t('shop.needGems'), { ...opts, variant: 'ghost' });
    buy.dataset.shop = `buy-${entry.id}`;
    // Marks a button that must stay disabled once the lock lifts — the lock/unlock pass over
    // the grid must never accidentally re-enable one that was already disabled for being
    // unaffordable.
    if (!entry.affordable) buy.dataset.shopUnaffordable = 'true';
    buy.setAttribute('aria-busy', String(isBusyHere));
    footer.append(buy);
  }
  stack.append(footer);
  tile.append(stack);

  return tile;
}

/**
 * The "Get Gems" section (PREMIUM currency — "Gems" to the player, `coins` on the wire) — a real-money top-up. `POST /topup/checkout` returns a `url` and a
 * `completed` flag rather than a status enum: the Mock provider (`testMode: true` on the
 * packages response) settles inline (`completed: true`, `url` its own confirmation page, never
 * followed), a real provider answers `completed: false` and the caller must hand off to `url`.
 *
 * Mounted OUTSIDE the marketplace grid and given its own `renderAsync`, for the same reason a
 * dead category must not blank the marketplace below it: a topup outage must not touch the
 * cosmetics grid, and a cosmetics-catalogue re-render must not tear this section down mid-purchase.
 * Not one of the rail's categories — the header's own Gems pill already deep-links here
 * (`#coins`), and it is a currency purchase, not a cosmetic.
 */
function mountCoinsSection(): { element: HTMLElement; dispose: () => void } {
  const section = document.createElement('section');
  section.id = 'coins';
  section.className = 'flex flex-col gap-3';

  let unmounted = false;
  /** `null` when nothing is in flight, otherwise the one package id being bought — same
   * single-flight discipline as the cosmetics grid's `busySkinId`. */
  let busyPackageId: string | null = null;
  let currentPackages: TopupPackage[] = [];
  /** Buy-any-amount terms from the same response; `null` until the packages have loaded. */
  let customTerms: TopupCustomTerms | null = null;
  /** The amount on the custom slider — kept across `redraw()`s so a busy/error transition does
   * not snap the slider back to its minimum under the player's thumb. */
  let customCoins = 0;
  /** Whether the packages response came back `testMode: true` — read by the confirm dialog so
   * the "no real charge" note shows there too, not only on the packages grid below it. */
  let testModeOn = false;
  /** ISO 4217 currency for price display — populated from the packages response, falls back
   * to EUR for backwards compatibility with backends that predate multicurrency. */
  let shopCurrency = 'EUR';
  let paymentMethods: PaymentMethodsResponse | null = null;

  function formatPrice(cents: number): string {
    // The interface language, not 'en-US' and not the machine's: a price is read on the same
    // screen as everything else, so a German UI gets "12,00 €" and an English one "€12.00".
    return formatNumber(cents / 100, { style: 'currency', currency: shopCurrency, minimumFractionDigits: 2 });
  }
  /** The `renderAsync` container, captured once content first paints — `redraw()` rebuilds the
   * grid inside it on every busy/success/error transition without re-running `load()`. */
  let body: HTMLElement | null = null;
  /** One pending retry timer, per the "the credit lands via an event listener a few ms after
   * the checkout response" note — never more than one in flight, and cleared on unmount so a
   * disposed section never calls back into a store after teardown. */
  let refreshRetryTimer: ReturnType<typeof setTimeout> | null = null;
  /** The gateway-return poll (`?topup=return`) — one timer, cleared on unmount like the other. */
  let sessionPollTimer: ReturnType<typeof setTimeout> | null = null;

  const status = document.createElement('p');
  status.dataset.topup = 'status';
  status.className = 'text-[13px] hidden';

  function setStatus(text: string, tone: 'ok' | 'error' | 'dim'): void {
    status.textContent = text;
    status.className = `text-[13px] ${tone === 'ok' ? 'text-cyan' : tone === 'dim' ? 'text-text-dim' : 'text-error'}`;
  }

  /** How many gems are stacked on a pack's plate, and how big each is, by the pack's rank in the
   * list (cheapest first): a bigger pack is a bigger pile. Presentation only — the amount the
   * pack grants is `pkg.coins`, from the server. */
  const GEM_STACKS: readonly (readonly number[])[] = [
    [56],
    [40, 58],
    [34, 52, 40],
    [32, 46, 58, 40],
    [34, 50, 64, 44],
  ];

  function gemStack(rank: number): HTMLElement {
    const stack = document.createElement('div');
    // Scaled down a touch on a phone, where the biggest pile would overflow a 2-up plate.
    stack.className = 'flex items-end justify-center h-[72px] origin-bottom scale-[0.85] sm:scale-100';
    for (const size of GEM_STACKS[Math.min(rank, GEM_STACKS.length - 1)]!) {
      stack.append(icon('premium', { size, className: '-mx-2 [filter:drop-shadow(0_4px_4px_rgba(0,0,0,0.6))]' }));
    }
    return stack;
  }

  function packageTile(pkg: TopupPackage, rank: number): HTMLElement {
    const isBusy = busyPackageId === pkg.id;
    const blockedByOther = busyPackageId !== null && !isBusy;

    // The GEM, not the gold coin: every pack grants PREMIUM. Drawing the credits coin on a
    // premium bundle was the exact misread the 2026-09-08 rename fixed.
    const gems = gemStack(rank);
    // The featured pack's pile idles; the plate itself cannot (`kit-plate-hit` owns its transform).
    if (pkg.best) gems.classList.add('kit-float');

    const name = document.createElement('p');
    name.className = 'kit-label font-heading uppercase text-[12px] truncate max-w-full';
    name.textContent = pkg.name;

    const amount = priceChip('PREMIUM', formatNumber(pkg.coins));
    amount.classList.add('[--kh:36px]');

    const price = document.createElement('p');
    price.className = 'kit-label font-heading text-[18px] tabular-nums';
    price.textContent = formatPrice(pkg.priceCents);

    const buy = button(isBusy ? t('common.buying') : t('common.buy'), {
      variant: 'primary',
      disabled: isBusy || blockedByOther,
      className: 'w-full mt-1',
      onClick: () => buyPackage(pkg),
    });
    buy.dataset.topup = `buy-${pkg.id}`;
    buy.setAttribute('aria-busy', String(isBusy));

    // `tag` is a marketing label ("Best value", "+10% gems"), never a bonus amount to compute
    // with — the server sends it pre-formatted. A `best` pack with no tag still says so.
    const ribbonText = pkg.tag ?? (pkg.best ? t('shop.bestValue') : null);

    const tile = document.createElement('div');
    tile.className = [
      'kit-plate kit-plate-hit [--kp-rim:14px] [--kp-pad:14px] w-full min-w-0 flex flex-col items-center gap-2 text-center',
      pkg.best ? 'kit-selected' : '',
    ]
      .filter(Boolean)
      .join(' ');
    tile.dataset.topup = `tile-${pkg.id}`;
    if (ribbonText) {
      const ribbon = badge(ribbonText, 'gold');
      ribbon.className += ' kit-pop absolute -top-3 left-1/2 -translate-x-1/2 z-[2] max-w-[90%]';
      tile.append(ribbon);
    }
    tile.append(gems, name, amount, price, buy);
    return tile;
  }

  /**
   * The buy-any-amount tile (2026-09-11; second pass the same day: the button was too big and
   * the slider too plain). One card, full grid width:
   *
   *   CUSTOM AMOUNT                         (label)
   *   💎 [ 2 500 ]   1K · 5K · 25K · 100K · 500K · 1M   (field + preset chips)
   *   ══════════●═══════════════════════   (purple slider, value bubble riding the thumb,
   *   ˙    ˙     ˙        ˙          ˙       tick marks at the presets)
   *   €25.00  any amount 100–1,000,000      [ BUY ]   (price left, compact Buy right)
   *
   * Price uses the server's `centsPerCoin` with the same rounding `customCostCents` charges, so
   * the tile shows what the receipt says. Bounds are the server's; the control only keeps the
   * value inside them and the server re-validates.
   */
  function customTile(terms: TopupCustomTerms): HTMLElement {
    const id = 'custom';
    const isBusy = busyPackageId === id;
    const blockedByOther = busyPackageId !== null && !isBusy;
    const step = 100;
    const min = Math.max(step, Math.ceil(terms.minCoins / step) * step);
    const max = Math.max(min, Math.floor(terms.maxCoins / step) * step);
    if (customCoins < min || customCoins > max) customCoins = Math.min(max, Math.max(min, 1000));
    const presets = [1000, 5000, 25_000, 100_000, 500_000, 1_000_000].filter((n) => n >= min && n <= max);
    // The slider is logarithmic and runs the whole min..max range (2026-09-30, ceiling raised to
    // €10,000). Linear, the amounts anyone actually buys sat in the first few percent of the
    // track — which is why it used to stop at 25K, and why the player read €250 as the maximum.
    // The track is SLIDER_STEPS positions; `toPos`/`fromPos` map between a position and gems.
    const SLIDER_STEPS = 1000;
    const span = Math.log(max / min) || 1;
    const toPos = (n: number): number => Math.round((Math.log(Math.max(min, n) / min) / span) * SLIDER_STEPS);
    const fromPos = (pos: number): number => {
      const raw = min * Math.exp((pos / SLIDER_STEPS) * span);
      // Round to a figure a person would pick: 100s under 10K, 1,000s under 100K, 10,000s above.
      const unit = raw < 10_000 ? step : raw < 100_000 ? 1000 : 10_000;
      return Math.min(max, Math.max(min, Math.round(raw / unit) * unit));
    };
    const short = (n: number): string =>
      n >= 1_000_000 ? `${(n / 1_000_000).toString().replace(/\.0$/, '')}M`
        : n >= 1000 ? `${(n / 1000).toString().replace(/\.0$/, '')}K` : String(n);

    // Row 1: the figure and the presets.
    const top = document.createElement('div');
    top.className = 'flex flex-wrap items-center gap-x-5 gap-y-2';
    const coinRow = document.createElement('div');
    // The figure sits in a well cut into the plate, not on a hairline.
    coinRow.className = 'kit-well flex items-center gap-1.5 px-3 text-purple-light font-heading font-extrabold text-[28px] tabular-nums';
    coinRow.append(icon('premium', { size: 28 }));
    const amount = document.createElement('input');
    amount.type = 'number';
    amount.inputMode = 'numeric';
    amount.min = String(min);
    amount.max = String(max);
    amount.step = String(step);
    amount.value = String(customCoins);
    amount.dataset.topup = 'custom-amount';
    amount.setAttribute('aria-label', t('shop.custom.title'));
    amount.className =
      'w-[7ch] bg-transparent text-purple-light font-heading font-extrabold text-[28px] tabular-nums ' +
      'outline-none focus-visible:outline-2 focus-visible:outline-cyan-bright min-h-[44px]';
    coinRow.append(amount);
    const chips = document.createElement('div');
    chips.className = 'flex flex-wrap gap-2';
    chips.dataset.topup = 'custom-presets';
    const chipButtons = new Map<number, HTMLButtonElement>();
    for (const n of presets) {
      const chip = button(short(n), {
        variant: 'selector',
        className: 'min-w-[3.5rem] px-3',
        ariaLabel: `${formatNumber(n)} ${t('common.gems')}`,
        onClick: () => {
          customCoins = n;
          amount.value = String(n);
          show();
        },
      });
      chip.dataset.topup = `custom-preset-${n}`;
      chipButtons.set(n, chip);
      chips.append(chip);
    }
    top.append(coinRow, chips);

    // Row 2: the slider, with a value bubble riding the thumb and tick marks at the presets.
    const track = document.createElement('div');
    track.className = 'relative pt-7 pb-4';
    const bubble = document.createElement('span');
    bubble.dataset.topup = 'custom-bubble';
    bubble.setAttribute('aria-hidden', 'true');
    // NO transition on `left`. The bubble is a label stuck to the thumb, and the thumb does not
    // animate — a 75ms ease on its position meant that during a drag the bubble was permanently
    // chasing it: measured on a fast drag, the style said 90.8% while the painted bubble was
    // still 908px behind the thumb. Reported as the value lagging the slider (2026-09-18); the
    // number was always right, the label was just somewhere else.
    bubble.className =
      'absolute top-0 -translate-x-1/2 text-[12px] font-heading font-bold tabular-nums ' +
      'kit-chip [--kh:26px] kit-label pointer-events-none whitespace-nowrap';
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '0';
    slider.max = String(SLIDER_STEPS);
    slider.step = '1';
    slider.value = String(toPos(customCoins));
    slider.dataset.topup = 'custom-slider';
    slider.setAttribute('aria-label', t('shop.custom.title'));
    slider.className = RANGE_CLASS_GEMS;
    const ticks = document.createElement('div');
    ticks.className = 'relative h-2 mx-2';
    ticks.setAttribute('aria-hidden', 'true');
    for (const n of presets) {
      const tick = document.createElement('span');
      const pct = (toPos(n) / SLIDER_STEPS) * 100;
      tick.className = 'absolute top-0 h-2 w-px bg-steel-200/70';
      tick.style.left = `${pct}%`;
      ticks.append(tick);
    }
    track.append(bubble, slider, ticks);

    // Row 3: the price with the bounds beside it, and a Buy that is a button, not a banner.
    const foot = document.createElement('div');
    foot.className = 'flex flex-wrap items-end justify-between gap-3';
    const priceCol = document.createElement('div');
    priceCol.className = 'flex flex-col gap-0.5 min-w-0';
    const price = document.createElement('p');
    price.className = 'kit-label text-[24px] font-heading tabular-nums leading-none';
    price.dataset.topup = 'custom-price';
    const hint = document.createElement('p');
    hint.className = 'text-[11px] text-text-dim';
    hint.textContent = t('shop.custom.hint', { min: formatNumber(min), max: formatNumber(max) });
    priceCol.append(price, hint);
    const buy = button(isBusy ? t('common.buying') : t('common.buy'), {
      variant: 'primary',
      disabled: isBusy || blockedByOther,
      className: 'w-full sm:w-auto sm:min-w-[11rem]',
      onClick: () => buyCustom(customCoins),
    });
    buy.dataset.topup = 'buy-custom';
    buy.setAttribute('aria-busy', String(isBusy));
    foot.append(priceCol, buy);

    const clamp = (raw: number): number => {
      if (!Number.isFinite(raw)) return min;
      return Math.min(max, Math.max(min, Math.round(raw / step) * step));
    };
    const show = (): void => {
      // Same rounding the server applies (`customCostCents`: round, floor at 1¢).
      const cents = Math.max(1, Math.round(customCoins * terms.centsPerCoin));
      price.textContent = formatPrice(cents);
      const onTrack = toPos(customCoins);
      slider.value = String(onTrack);
      syncRangeFill(slider);
      // The bubble sits ON the thumb, measured, and is then kept inside the track.
      //
      // It used to be `calc(pct% + (50 - pct) * 0.16px)`: a percentage of the track plus a fudge
      // worth at most 8px. Two things wrong with that. The thumb's centre does not travel 0-100%
      // of the track — it travels from half a thumb to width minus half a thumb — and the bubble
      // is ~60px wide with `-translate-x-1/2`, so at either end of the slider half of it hung
      // outside the card and was clipped. `thumbCenterPx` is the thumb's real travel, and the
      // clamp holds the bubble's own half-width in from both edges, so at the extremes it stops
      // over the end of the track instead of walking off it.
      bubble.textContent = formatNumber(customCoins);
      const trackWidth = slider.clientWidth;
      const centre = slider.offsetLeft + thumbCenterPx(slider);
      if (trackWidth) {
        const half = bubble.offsetWidth / 2;
        const left = slider.offsetLeft;
        bubble.style.left = `${Math.min(Math.max(centre, left + half), left + trackWidth - half)}px`;
      } else {
        // No layout to measure (jsdom, first paint): the fraction is still the honest answer.
        const pct = (onTrack / SLIDER_STEPS) * 100;
        bubble.style.left = `${pct}%`;
      }
      for (const [n, chip] of chipButtons) chip.dataset.pressed = String(n === customCoins);
      buy.disabled = isBusy || blockedByOther;
    };
    slider.addEventListener('input', () => {
      customCoins = fromPos(Number(slider.value));
      amount.value = String(customCoins);
      show();
    });
    // `change`, not `input`: clamping a half-typed "5" to the minimum on every keystroke would
    // fight the player; the value settles when the field blurs.
    amount.addEventListener('change', () => {
      customCoins = clamp(Number(amount.value));
      amount.value = String(customCoins);
      show();
    });
    show();

    const tile = card([top, track, foot], {
      title: t('shop.custom.title'),
      className: 'w-full col-span-2 sm:col-span-3 lg:col-span-4 flex flex-col gap-3',
    });
    tile.dataset.topup = 'tile-custom';
    return tile;
  }

  function redraw(): void {
    if (!body) return;
    body.innerHTML = '';
    const grid = document.createElement('div');
    grid.dataset.topup = 'grid';
    // Five columns at `xl`, because five is how many packages there are — at four, the fifth
    // sat alone on a second row, which reads as a grid that failed to finish loading. Same
    // orphan rule the operator grid already follows.
    grid.className = 'grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';
    currentPackages.forEach((pkg, rank) => grid.append(packageTile(pkg, rank)));
    if (customTerms && customTerms.maxCoins >= customTerms.minCoins && customTerms.centsPerCoin > 0) {
      grid.append(customTile(customTerms));
    }
    body.append(grid);
    deal(grid);
  }

  /** Test-mode note for the confirm dialog — a fresh node per call, since `extra` nodes are
   * appended (moved) into the dialog rather than cloned, and `testModeNote` above is already
   * planted in the packages grid. */
  function testModeConfirmNote(): HTMLElement | null {
    if (!testModeOn) return null;
    const note = document.createElement('p');
    note.className = 'text-[12px] text-text-dim';
    note.textContent = t('shop.testMode');
    return note;
  }

  function openCheckoutModal(opts: {
    gems: number;
    priceCents: number;
    tileId: string;
    product: { productId?: string; amountCents?: number };
  }): void {
    if (!paymentMethods || paymentMethods.deposit.length === 0) {
      setStatus(t('shop.purchaseFailed'), 'error');
      return;
    }
    const methods = paymentMethods;
    let destroyed = false;
    let close: () => void = () => {};
    let pickerHandle: PaymentMethodPickerHandle | null = null;
    let currencyHandle: CurrencySelectorHandle | null = null;
    const teardown = () => {
      if (destroyed) return;
      destroyed = true;
      pickerHandle?.destroy();
      currencyHandle?.destroy();
      close();
    };

    const wrap = document.createElement('div');
    wrap.className = 'flex flex-col gap-4';

    const summary = document.createElement('div');
    summary.className = 'flex items-center gap-3';
    summary.append(icon('premium', { size: 40 }));
    const summaryText = document.createElement('p');
    summaryText.className = 'text-[15px] text-text';
    summaryText.textContent = t('confirmDialog.topupBody', {
      gems: formatNumber(opts.gems),
      price: formatPrice(opts.priceCents),
    });
    summary.append(summaryText);
    wrap.append(summary);

    const note = testModeConfirmNote();
    if (note) wrap.append(note);

    let selectedCurrency = methods.defaultCurrency;
    if (methods.currencies.length > 1) {
      const currencyEl = document.createElement('div');
      currencyHandle = mountCurrencySelector(currencyEl, {
        currencies: methods.currencies,
        value: selectedCurrency,
        methods: methods.deposit,
        onChange: (c) => {
          selectedCurrency = c;
          pickerHandle?.setCurrency(c);
        },
      });
      wrap.append(currencyEl);
    }

    const pickerEl = document.createElement('div');
    pickerHandle = mountPaymentMethodPicker(pickerEl, {
      methods: methods.deposit,
      currency: selectedCurrency,
      selected: methods.deposit[0]?.id ?? null,
      showDisabled: false,
      onSelect: () => { payBtn.disabled = !pickerHandle?.getSelected(); },
      onCurrencySwitch: (c) => {
        selectedCurrency = c;
        currencyHandle?.setValue(c);
      },
    });
    const hideIcons = document.createElement('style');
    hideIcons.textContent = '[data-method] img { display: none !important; }';
    wrap.append(hideIcons, pickerEl);

    const errorLine = document.createElement('p');
    errorLine.className = 'text-[13px] text-error hidden';
    errorLine.dataset.checkout = 'error';
    wrap.append(errorLine);

    const actions = document.createElement('div');
    actions.className = 'flex justify-end gap-3 pt-1';
    const cancelBtn = button(t('common.cancel'), { variant: 'secondary', onClick: () => teardown() });
    cancelBtn.dataset.checkout = 'cancel';
    const confirmLabel = t('common.confirm');
    const payBtn = button(confirmLabel, {
      variant: 'primary',
      disabled: !pickerHandle?.getSelected(),
      onClick: () => {
        const sel = pickerHandle?.getSelected();
        if (!sel || payBtn.disabled) return;
        playSample('select');

        payBtn.disabled = true;
        const inner = payBtn.querySelector('[data-plate-label]');
        if (inner) inner.textContent = t('common.loading');
        cancelBtn.disabled = true;
        errorLine.classList.add('hidden');

        void (async () => {
          try {
            const result = await createDeposit({
              method: sel.id,
              currency: selectedCurrency,
              ...opts.product,
              browser: collectBrowserInfo(),
              returnPath: '/shop',
            });
            if (unmounted) return;
            try { sessionStorage.setItem(PENDING_ORDER_KEY, result.orderId); } catch { /* private mode */ }
            location.assign(result.redirectUrl);
          } catch (error) {
            if (unmounted) return;
            if (isProfileNameRequired(error)) {
              teardown();
              let saved = false;
              try { saved = await promptProfileName(); } catch {
                if (!unmounted) setStatus(t('shop.nameRequired.saveFailed'), 'error');
                return;
              }
              if (saved && !unmounted) {
                setTimeout(() => {
                  if (!unmounted) openCheckoutModal(opts);
                }, 0);
              }
              return;
            }
            payBtn.disabled = false;
            const inner2 = payBtn.querySelector('[data-plate-label]');
            if (inner2) inner2.textContent = confirmLabel;
            cancelBtn.disabled = false;
            errorLine.textContent =
              error instanceof Error && 'status' in error && (error as { status: number }).status === 503
                ? t('shop.previewDisabled')
                : error instanceof Error && error.message
                  ? error.message
                  : t('shop.purchaseFailed');
            errorLine.classList.remove('hidden');
          }
        })();
      },
    });
    payBtn.dataset.checkout = 'pay';
    actions.append(cancelBtn, payBtn);
    wrap.append(actions);

    close = openModal({
      title: t('confirmDialog.topupTitle'),
      content: [wrap],
      onClose: () => teardown(),
    });
  }

  function buyPackage(pkg: TopupPackage): void {
    if (busyPackageId !== null) return;
    openCheckoutModal({ gems: pkg.coins, priceCents: pkg.priceCents, tileId: pkg.id, product: { productId: pkg.id } });
  }

  function buyCustom(coins: number): void {
    if (busyPackageId !== null || !customTerms) return;
    const terms = customTerms;
    const cents = Math.max(1, Math.round(coins * terms.centsPerCoin));
    openCheckoutModal({ gems: coins, priceCents: cents, tileId: 'custom', product: { amountCents: cents } });
  }

  function handleGatewayReturn(): void {
    // Core deposit flow: ?order=<orderId> (appended by the backend's returnPath).
    const coreOrderId = readReturnedOrderId();
    if (coreOrderId) {
      history.replaceState(null, '', location.pathname + location.hash);
      clearPendingOrder();
      setStatus(t('shop.paymentProcessing'), 'dim');
      void (async () => {
        try {
          const order = await pollDepositStatus(coreOrderId, { intervalMs: 3000, maxTries: 20 });
          if (unmounted) return;
          if (order.status === 'APPROVED') {
            playSample('collect');
            setStatus(t('shop.gemsAdded', { gems: '' }), 'ok');
            void refreshPlayerProgress();
          } else {
            setStatus(t('shop.paymentFailed'), 'error');
          }
        } catch {
          if (!unmounted) setStatus(t('shop.paymentFailed'), 'error');
        }
      })();
      return;
    }

    // Legacy topup flow (transition): ?topup=return&session=<id>
    const params = new URLSearchParams(location.search);
    const outcome = params.get('topup');
    if (outcome !== 'return' && outcome !== 'cancel') return;
    history.replaceState(null, '', location.pathname + location.hash);

    let sessionId = params.get('session');
    try {
      sessionId ??= sessionStorage.getItem('bl_pay_session');
      sessionStorage.removeItem('bl_pay_session');
    } catch { /* private mode */ }

    if (outcome === 'cancel') {
      setStatus(t('shop.paymentCancelled'), 'dim');
      return;
    }
    if (!sessionId) {
      setStatus(t('shop.paymentUnknown'), 'error');
      return;
    }

    const MAX_POLLS = 20;
    const POLL_MS = 3_000;
    let polls = 0;
    setStatus(t('shop.paymentProcessing'), 'dim');

    const poll = async (): Promise<void> => {
      if (unmounted) return;
      polls += 1;
      try {
        const result = await fetchTopupSessionStatus(sessionId);
        if (unmounted) return;
        if (result.status === 'completed') {
          playSample('collect');
          setStatus(t('shop.gemsAdded', { gems: formatNumber(result.coins) }), 'ok');
          void refreshPlayerProgress();
          return;
        }
        if (result.status === 'expired' || polls >= MAX_POLLS) {
          setStatus(t('shop.paymentFailed'), 'error');
          return;
        }
      } catch (error) {
        if (unmounted) return;
        if ((error instanceof ApiError && error.status === 404) || polls >= MAX_POLLS) {
          setStatus(error instanceof ApiError && error.status === 404 ? t('shop.paymentUnknown') : t('shop.paymentFailed'), 'error');
          return;
        }
      }
      sessionPollTimer = setTimeout(() => { sessionPollTimer = null; void poll(); }, POLL_MS);
    };
    void poll();
  }

  const heading = sectionHeading(t('shop.getGems'), { level: 'h2' });
  const testModeNote = document.createElement('p');
  testModeNote.dataset.topup = 'test-mode';
  testModeNote.className = 'text-[12px] text-text-dim hidden';
  testModeNote.textContent = t('shop.testMode');
  const contentBody = document.createElement('div');
  section.append(heading, testModeNote, status, contentBody);
  handleGatewayReturn();

  const disposeAsync = renderAsync<TopupPackagesView>(contentBody, {
    load: () => fetchTopupPackages(),
    isEmpty: (view) => view.packages.length === 0,
    renderEmpty(view) {
      view.innerHTML = '';
      view.append(notice(t('shop.noPackages'), 'dim'));
    },
    renderError(view, error, retry) {
      view.innerHTML = '';
      const wrap = document.createElement('div');
      wrap.className = 'flex flex-col items-start gap-2';
      wrap.append(notice(error instanceof Error ? error.message : t('shop.packagesFailed'), 'error'), button(t('common.retry'), { variant: 'secondary', onClick: retry }));
      view.append(wrap);
    },
    renderContent(packagesView, view) {
      body = view;
      currentPackages = packagesView.packages;
      customTerms = packagesView.custom ?? null;
      testModeOn = packagesView.testMode;
      shopCurrency = packagesView.currency ?? 'EUR';
      // Never hardcoded — the note only shows because the server said so, and disappears the
      // moment a real provider is configured and `testMode` flips to `false`.
      testModeNote.className = `text-[12px] text-text-dim ${packagesView.testMode ? '' : 'hidden'}`;
      if (!paymentMethods) {
        getPaymentMethods()
          .then((m) => { if (!unmounted) { paymentMethods = m; shopCurrency = m.defaultCurrency; } })
          .catch(() => {});
      }
      redraw();
      requestAnimationFrame(() => revealOnMount(view));
    },
  });

  return {
    element: section,
    dispose() {
      unmounted = true;
      if (refreshRetryTimer !== null) clearTimeout(refreshRetryTimer);
      if (sessionPollTimer !== null) clearTimeout(sessionPollTimer);
      disposeAsync();
      body = null;
    },
  };
}

/** The rail's categories, in the order the brief specifies. `all` and `operators` are handled
 * specially by `mountShopRoute`; the rest are the six `MarketSection` ids. */
type MarketTabId = 'all' | 'operators' | 'gems' | MarketCategoryId;

export const mountShopRoute: RouteMount = (outlet) => {
  const root = document.createElement('div');
  root.className = 'kit-window flex flex-col gap-4';

  const coins = mountCoinsSection();
  // A TAB, not the top of the page. Mounted above the shop's own title, the gem store was the
  // first and largest thing on the screen — five packages, a custom-amount panel and a price
  // in euros before a single item for sale. A player opening a shop is there to look at goods;
  // topping up is what they do once they know what they want.
  outlet.append(root);

  // Deep link from the header's credits button (`shell.ts`: `navigate('/shop#coins')`) — the
  // hash never changes `location.pathname`, so a player already on `/shop` gets no fresh mount
  // to key this off; it only fires for an actual navigation onto this route, which is the case
  // that matters (the button is the header's, always visible, so re-clicking it while already
  // here has nothing further to scroll to).
  // Deep link from the header's credits button: open the Gems tab rather than scroll to a
  // block, now that the block is a tab. Deferred so the strip exists by the time it runs.
  const wantsGems = location.hash === '#coins';

  let unmounted = false;
  /** `null` when nothing is in flight, otherwise the one skin id whose purchase is pending —
   * keyed by id (not a bare boolean) so `shopTile` can tell "busy here" from "busy elsewhere". */
  let busySkinId: number | null = null;
  /** The rows behind the grid currently on screen — read to name a purchase in its burst and to
   * rebuild a single tile without re-fetching. */
  let catalogueSkins: ShopEntry[] = [];
  /** Every tile currently in the DOM, by skin id — how a purchase patches one card in place
   * instead of the whole grid tearing down and rebuilding around it. */
  const tileEls = new Map<number, HTMLElement>();
  /** Every operator grid currently on screen, so the "All" tab can show a preview of the first
   * few and the Operators tab the whole catalogue, without rebuilding either. */
  let operatorGrids: HTMLElement[] = [];
  /** How many operators the curated front page shows before "View all" — two full rows at the
   * widest break, one on a phone. */
  const OPERATOR_PREVIEW = 8;
  /** The balance readout's own text node — patched directly on purchase rather than pulled back
   * into a full `draw()`, which is the "silent re-render that loses scroll" this pass removes. */
  let balanceValueEl: HTMLElement | null = null;
  /** Kept alongside `balanceValueEl`'s own text — the confirm dialog needs the number itself
   * (to compute "balance after"), not the formatted string already sitting in the DOM. */
  let premiumBalance = 0;

  // The window's name on a hazard-capped plate, with the shop's own icon.
  const pageHeading = document.createElement('div');
  pageHeading.className = 'kit-header';
  const pageTitle = document.createElement('h1');
  pageTitle.className = 'kit-title font-heading uppercase text-2xl flex items-center gap-2';
  pageTitle.append(icon('shop', { size: 32, className: 'kit-float' }), document.createTextNode(t('shop.title')));
  pageHeading.append(pageTitle);

  /** The Operators pane — everything `draw()`/`buy()` below already built, unchanged in
   * substance, just re-targeted at this pane instead of the whole route (`root`). */
  const operatorsPane = document.createElement('div');
  operatorsPane.dataset.shop = 'operators-pane';

  function draw(catalogue: ShopCatalogue): void {
    if (unmounted) return;
    catalogueSkins = catalogue.skins;
    operatorsPane.replaceChildren();
    tileEls.clear();

    // The balance is a number the player checks against every price on the screen, so it is
    // drawn as the same coin those prices carry rather than described in a sentence that
    // starts with the word "Balance".
    const balance = document.createElement('p');
    balance.dataset.shop = 'balance';
    // Gems, not credits: since 2026-09-09 every skin here is a gem price (the level and rank
    // gates are the free route to the same bodies). A chip with the gem, not a sentence.
    balance.className = 'kit-chip [--kh:36px] self-start mb-1';
    balance.setAttribute('aria-label', `${t('common.gems')}: ${formatNumber(catalogue.progress.premiumBalance)}`);
    balance.append(icon('premium', { size: 26, className: '-ml-2 [filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' }));
    const balanceValue = document.createElement('span');
    balanceValue.className = 'kit-label font-heading text-[16px] tabular-nums';
    balanceValue.textContent = formatNumber(catalogue.progress.premiumBalance);
    balanceValueEl = balanceValue;
    premiumBalance = catalogue.progress.premiumBalance;
    balance.append(balanceValue);

    // Purchasable first (the storefront's whole point), then owned as a receipt of what
    // credits already bought.
    const forSale = catalogue.skins.filter((s) => s.unlock.kind !== 'default' && !s.owned);
    const ownedPurchases = catalogue.skins.filter((s) => s.unlock.kind !== 'default' && s.owned);
    // Everything else in the catalogue — the level and rank unlocks. They used to be hidden
    // here on the reasoning that a price you cannot pay in currency reads as a bug. True of a
    // *price*; not true of the skins themselves, and hiding them left a storefront showing two
    // cards on a 1440px screen while three-fifths of the game's cosmetics went unmentioned.
    // They appear as `EARN` cards with the requirement where the price would be: a shop that
    // shows the whole range and says which parts money cannot buy is more honest than one that
    // silently omits them, and it is the only screen a player looks at when asking "what is
    // there to get".
    // Nothing is gems-only-locked any more: every unowned skin sits in `forSale`. Kept as an
    // (empty) list so the range note below keeps its shape.
    const earnable: ShopEntry[] = [];

    // ONE grid, not a section per unlock kind. Splitting them gave a 1440px screen two rows of
    // two cards each with a heading between, when the whole range is five items and fits in a
    // single row — and the split implied the earnable skins were a different *kind* of thing
    // when the only difference is what they cost. The order carries that instead: what credits
    // can buy today, then what play unlocks, then what credits already bought. Five columns at
    // `xl` because five is how many skins there are; a four-column track would orphan one.
    const grid = document.createElement('div');
    grid.dataset.shop = 'grid';
    grid.className = OPERATOR_GRID_CLASS;
    for (const entry of [...forSale, ...earnable]) {
      const tile = shopTile(entry, busySkinId, buy);
      tileEls.set(entry.id, tile);
      grid.append(tile);
    }
    operatorGrids = [grid];

    // No paragraph of explanation under the balance. It read as a disclaimer above the goods —
    // and every sentence in it is already said by the cards themselves: a price where there is
    // one, a requirement where there is not.
    // On the curated front page the operators are a PREVIEW like every other category, with
    // their own heading and their own way through. Uncapped, this one grid was thirty-nine
    // cards — a 375px screen scrolled sixteen thousand pixels before reaching anything else.
    operatorsPane.append(balance, sectionHeading(t('shop.tabs.operators'), {
      level: 'h2',
      className: 'mb-2',
      trailing: viewAllButton('operators'),
    }), grid);
    // The screen arriving, not silently painting — one call, on the grid the player actually
    // scans first. Re-run on every full `draw()` (mount and any hard reload), never from the
    // in-place purchase patch below, which is the whole point of that patch existing.
    revealOnMount(grid);
    deal(grid);

    if (forSale.length === 0) {
      // A full-width line under the balance, not a card dropped into the grid — as a grid child
      // it took one column and stood there as a narrow ragged block beside nothing.
      grid.remove();
      operatorsPane.append(notice(t('shop.soldOut'), 'dim'));
    }

    if (ownedPurchases.length > 0) {
      // No "OWNED" sub-heading. Nested under the OPERATORS heading it read as a second
      // section of the same category, when the only difference is state the tiles already
      // carry themselves — an OWNED badge in the corner and "equip in Loadout" in the footer.
      const ownedGrid = document.createElement('div');
      ownedGrid.className = OPERATOR_GRID_CLASS;
      for (const entry of ownedPurchases) {
        const tile = shopTile(entry, busySkinId, buy);
        tileEls.set(entry.id, tile);
        ownedGrid.append(tile);
      }
      operatorsPane.append(ownedGrid);
      deal(ownedGrid);
      operatorGrids.push(ownedGrid);
    }

    // The catalogue arrives after the first `selectTab`, so the cap has to be re-applied here
    // as well — otherwise the front page paints the whole roster once and only trims it when
    // the player next touches a tab.
    applyOperatorPreview();
  }

  /** Rebuilds exactly one tile from a fresh `ShopEntry` and swaps it in at its existing DOM
   * position — a purchase's whole visible effect on the grid, short of the balance line. */
  function swapTile(entry: ShopEntry): HTMLElement {
    const next = shopTile(entry, busySkinId, buy);
    tileEls.get(entry.id)?.replaceWith(next);
    tileEls.set(entry.id, next);
    return next;
  }

  /** Disables (or restores) every Buy button except `exceptId` — the busy lock made legible
   * across the whole grid, not just on the button that was pressed. Buttons already disabled
   * for being unaffordable are left alone in both directions. */
  function setOtherBuyButtonsLocked(exceptId: number, locked: boolean): void {
    for (const el of operatorsPane.querySelectorAll<HTMLButtonElement>('[data-shop^="buy-"]')) {
      if (el.dataset.shop === `buy-${exceptId}`) continue;
      if (el.dataset.shopUnaffordable === 'true') continue;
      el.disabled = locked;
    }
  }

  /** The one motion a successful purchase owes its own badge — jsdom has neither
   * `Element.animate` nor `matchMedia` by default, so both are guarded the same way `fx.ts`
   * guards its own WAAPI calls rather than assuming either exists. */
  function pulseBadge(el: Element | null): void {
    if (!(el instanceof HTMLElement) || typeof el.animate !== 'function') return;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    el.animate(
      [{ transform: 'scale(0.85)' }, { transform: 'scale(1.18)', offset: 0.55 }, { transform: 'scale(1)' }],
      { duration: 380, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
    );
  }

  function buy(skinId: number): void {
    if (busySkinId !== null) return;
    const bought = catalogueSkins.find((entry) => entry.id === skinId);
    if (!bought || bought.unlock.kind === 'default') return;
    const gems = bought.unlock.gemCost;

    void (async () => {
      const confirmed = await confirmAction({
        title: t('confirmDialog.buyTitle'),
        body: t('confirmDialog.buyBody', { item: bought.name, price: `${formatNumber(gems)} gems` }),
        detail: t('confirmDialog.balanceAfter', { balance: formatNumber(Math.max(0, premiumBalance - gems)) }),
        art: stillImg({
          src: skinStillPath(bought.id),
          alt: bought.name,
          width: 64,
          height: 96,
          className: 'rounded-sm object-cover',
        }),
      });
      // No busy state, no status line — Cancel/Escape/backdrop leaves the tile exactly as it was.
      if (!confirmed || busySkinId !== null) return;

      busySkinId = skinId;
      // The pressed card flips to its busy state immediately — label swap, disabled — rather
      // than waiting for the request to resolve before showing anything happened at all.
      swapTile(bought);
      setOtherBuyButtonsLocked(skinId, true);

      try {
        await api.post('/cosmetics/purchase', { skin: skinId });
        playSample('collect');
        const catalogue = await api.get<ShopCatalogue>('/cosmetics');
        catalogueSkins = catalogue.skins;
        busySkinId = null;
        const updated = catalogue.skins.find((entry) => entry.id === skinId);
        // The card flips to its Owned state IN PLACE — this patches the one tile that changed
        // rather than calling `draw()`, which tore down and rebuilt every card on every
        // purchase: the grid's scroll position and every other card's hover/focus state used
        // to reset with it. The trade this accepts: the purchased card stays wherever it was
        // drawn rather than jumping down into the "Owned" shelf mid-session — the categoriser
        // only re-sorts it there on the next full `draw()` (a re-navigation), which is a small
        // staleness against a card teleporting out from under the pointer that just bought it.
        if (updated) {
          const tile = swapTile(updated);
          const badgeEl = tile.querySelector(`[data-shop="badge-${skinId}"]`);
          pulseBadge(badgeEl);
          // Five thousand credits used to leave the account in silence, with a card quietly
          // changing state as the only acknowledgement. The name is the label rather than the
          // price: what the player just gained is the skin — the price leaving is already
          // counted down by the balance line below.
          if (badgeEl) rewardBurst(badgeEl, { label: updated.name, tone: 'cyan' });
        }
        setOtherBuyButtonsLocked(skinId, false);
        premiumBalance = catalogue.progress.premiumBalance;
        if (balanceValueEl) balanceValueEl.textContent = formatNumber(catalogue.progress.premiumBalance);
        // The header's gems pill must not go on showing the pre-purchase balance.
        void refreshPlayerProgress();
      } catch (error) {
        busySkinId = null;
        if (unmounted) return;
        // The busy label was optimistic, not final — back to Buy on the same, unpurchased
        // entry.
        if (bought) swapTile(bought);
        setOtherBuyButtonsLocked(skinId, false);
        operatorsPane.prepend(notice(t('shop.purchaseFailedDetail', { message: (error as Error).message }), 'error'));
      }
    })();
  }

  const disposeAsync = renderAsync(operatorsPane, {
    load: () => api.get<ShopCatalogue>('/cosmetics'),
    renderContent: (catalogue) => draw(catalogue),
  });

  // ── The other six categories ────────────────────────────────────────────────────────────────
  //
  // Each owns its own fetch and its own single-flight busy lock (`marketplace-section.ts`), so a
  // dead category only ever hides its own tab, and a busy purchase in one never freezes another.
  const sections: MarketSection[] = [
    createWeaponSkinsSection(),
    createCharmsSection(),
    createSpraysSection(),
    createEmotesSection(),
    createPlayerCardsSection(),
    createTitlesSection(),
    // Last on the rail, and deliberately not a catalogue: what the player already owns, priced
    // to sell back. It rides the same rail because owning and pricing are one conversation —
    // this is what the Locker screen turned into (`design/gdd/meta-screens.md`).
    createSellSection(),
  ];

  const marketPane = document.createElement('div');
  marketPane.dataset.market = 'pane';
  marketPane.className = 'flex flex-col gap-6';

  function feedbackNote(message: string): HTMLElement {
    const p = notice(message, 'error');
    p.setAttribute('role', 'status');
    return p;
  }

  /** Grids already dealt in on this tab, so a busy toggle's re-render does not replay the deal. */
  const dealt = new Set<string>();
  function dealOnce(key: string, grid: HTMLElement): void {
    if (dealt.has(key) || grid.children.length === 0) return;
    dealt.add(key);
    deal(grid);
  }

  function selectTab(id: MarketTabId): void {
    dealt.clear();
    activeTab = id;
    tabs.setActive(id);
    operatorsPane.hidden = !(id === 'all' || id === 'operators');
    marketPane.hidden = id === 'operators' || id === 'gems';
    coins.element.hidden = id !== 'gems';
    applyOperatorPreview();
    renderMarketPane();
  }

  /** Hides the tail of the operator grids on the front page. Hidden, not unbuilt: switching to
   * the Operators tab then reveals them with no fetch and no rebuild. */
  function applyOperatorPreview(): void {
    const capped = activeTab === 'all';
    let shown = 0;
    for (const grid of operatorGrids) {
      for (const child of [...grid.children] as HTMLElement[]) {
        const hide = capped && shown >= OPERATOR_PREVIEW;
        child.hidden = hide;
        if (!hide) shown++;
      }
    }
  }

  function viewAllButton(id: MarketTabId): HTMLElement {
    return button(t('shop.viewAll'), {
      variant: 'ghost',
      className: 'text-[11px] px-3',
      onClick: () => selectTab(id),
    });
  }

  function renderMarketPane(): void {
    marketPane.replaceChildren();
    if (activeTab === 'operators') return;

    if (activeTab === 'all') {
      // A curated front page: the full Operators grid lives in its own pane above this one
      // (always shown alongside "All"), and this adds a short preview row per category that
      // currently has anything to show — a category still loading, empty, or whose own fetch
      // failed contributes nothing rather than an empty heading.
      for (const section of sections) {
        // Sell is not shopping, so it never appears on the front page — a curated row of the
        // player's own belongings under a heading that sells things would read as a shop
        // offering them back their own kit.
        if (section.id === 'sell') continue;
        const items = section.getItems();
        if (items.length === 0) continue;
        const heading = sectionHeading(section.label, { level: 'h2', className: 'mb-2', trailing: viewAllButton(section.id) });
        const row = document.createElement('div');
        row.dataset.market = `row-${section.id}`;
        row.className = MARKET_GRID_CLASS;
        for (const item of items.slice(0, 4)) row.append(marketTile(item));
        marketPane.append(heading, row);
        dealOnce(`row-${section.id}`, row);
        const feedback = section.getFeedback();
        if (feedback) marketPane.append(feedbackNote(feedback));
      }
      return;
    }

    const section = sections.find((candidate) => candidate.id === activeTab);
    if (!section) return;
    // What this category is, and where the player will see it — above the grid, once, rather
    // than repeated on forty tiles. Only on a category's own tab: the "All" view is a browse,
    // and six explanatory lines down it would be an essay, not a storefront.
    if (section.note) {
      const note = notice(section.note, 'dim');
      note.dataset.market = `note-${section.id}`;
      marketPane.append(note);
    }
    // A category may draw its own pane instead of the shared grid — see `MarketSection.renderPane`.
    // Sell does, because it is a ledger of what you own rather than a catalogue of what you want.
    if (section.renderPane) {
      section.renderPane(marketPane);
      const own = section.getFeedback();
      if (own) marketPane.append(feedbackNote(own));
      return;
    }
    const items = section.getItems();
    const grid = document.createElement('div');
    grid.dataset.market = `grid-${section.id}`;
    grid.className = MARKET_GRID_CLASS;
    for (const item of items) grid.append(marketTile(item));
    marketPane.append(grid);
    dealOnce(`grid-${section.id}`, grid);
    const feedback = section.getFeedback();
    if (feedback) marketPane.append(feedbackNote(feedback));
    if (items.length === 0 && section.isReady() && !section.hasFailed()) {
      marketPane.append(notice(t('shop.market.empty'), 'dim'));
    }
  }

  function updateTabVisibility(): void {
    for (const section of sections) tabs.setVisible(section.id, !section.hasFailed());
  }

  let activeTab: MarketTabId = 'all';
  const tabs = tabStrip(
    [
      { id: 'all', label: t('shop.tabs.all'), art: TAB_ART.all },
      { id: 'operators', label: t('shop.tabs.operators'), art: TAB_ART.operators },
      ...sections.map((section) => ({ id: section.id, label: section.label, art: TAB_ART[section.id] })),
      // Last on the rail: buying the currency is what a player does once they know what they
      // want, so it sits after the goods rather than in front of them.
      { id: 'gems', label: t('shop.tabs.gems'), art: TAB_ART.gems },
    ],
    activeTab,
    (id) => selectTab(id as MarketTabId),
  );

  for (const section of sections) {
    section.subscribe(() => {
      updateTabVisibility();
      renderMarketPane();
    });
  }
  updateTabVisibility();
  renderMarketPane();

  root.append(pageHeading, tabs.el, operatorsPane, marketPane, coins.element);
  selectTab(wantsGems ? 'gems' : 'all');

  return () => {
    unmounted = true;
    disposeAsync();
    for (const section of sections) section.dispose();
    coins.dispose();
    coins.element.remove();
    root.remove();
  };
};
